import { createContext, useContext, useMemo } from "react";
import { useAuth, usePlan } from "@/features/auth";
import { hasFeatureAccess } from "@/features/notifications/config/notification.config";
import { useNotifications } from "@/features/notifications/hooks/useNotifications";
import type { NotificationsResponse } from "@/features/notifications/types/notifications.type";

type NotificationsContextValue = {
  notifications: NotificationsResponse | undefined
  totalCount: number
  isLoading: boolean
  isError: boolean
};

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export const NotificationsProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const plan = usePlan();
  const { data: notifications, isLoading, isError } = useNotifications({ enabled: isAuthenticated });

  const totalCount = useMemo(() => {
    if (!notifications) return 0;

    let count = 0;

    if (hasFeatureAccess("exchanges", plan)) {
      count += notifications.exchanges?.count ?? 0;
    }

    if (hasFeatureAccess("channels", plan)) {
      count += notifications.channels?.count ?? 0;
    }

    if (hasFeatureAccess("lottery_prizes", plan)) {
      count += notifications.lottery_prizes?.count ?? 0;
    }

    return count;
  }, [notifications, plan]);

  const value = useMemo(
    () => ({
      notifications,
      totalCount,
      isLoading,
      isError,
    }),
    [notifications, totalCount, isLoading, isError],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = (): NotificationsContextValue => {
  const context = useContext(NotificationsContext);

  if (!context) {
    return {
      notifications: undefined,
      totalCount: 0,
      isLoading: false,
      isError: true,
    };
  }

  return context;
};
